import axios, { AxiosError } from 'axios';
import { User } from '../hooks/useAuth';
import { jwtDecode } from 'jwt-decode';
import { businessService } from './businessService';
import { DEFAULT_THEME } from 'constants/themeDefaults';
import { apiClient } from './apiClient';
import { LoginResponse } from 'types/auth';
import { apiHealthService } from './apiHealthService';

const API_URL = process.env.REACT_APP_API_URL || `${apiHealthService.getApiUrl()}/api`;

interface JwtPayload {
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress': string;
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname': string;
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname': string;
    BusinessId: string;
    IsAdmin: string;
    businessRoles?: string;
    exp: number;
    iss: string;
    aud: string;
}

interface RefreshResponse {
    token: string;
    refreshToken: string;
    expiresIn: number;
    tokenType: string;
    user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
    };
}

export class AuthService {
    private readonly TOKEN_KEY = 'token';
    private readonly REFRESH_TOKEN_KEY = 'refreshToken';
    private readonly USER_KEY = 'user';
    private readonly EXPIRES_AT_KEY = 'expiresAt';
    private readonly BUSINESS_THEME_KEY = 'businessTheme';
    private readonly BUSINESS_THEME_ID_KEY = 'businessThemeId';
    private refreshPromise: Promise<string> | null = null;

    getToken(): string | null {
        return localStorage.getItem(this.TOKEN_KEY);
    }

    getRefreshToken(): string | null {
        return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    }

    setToken(token: string): void {
        localStorage.setItem(this.TOKEN_KEY, token);
    }

    removeToken(): void {
        localStorage.removeItem(this.TOKEN_KEY);
    }

    private shouldRefreshToken(decoded: JwtPayload): boolean {
        const expirationTime = decoded.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now(); // Simpler and more accurate
        // Refresh if token expires in less than 5 minutes
        const timeUntilExpiry = expirationTime - currentTime;
        console.log('Token expiry check:', {
            expirationTime: new Date(expirationTime).toISOString(),
            currentTime: new Date(currentTime).toISOString(),
            timeUntilExpiry: Math.floor(timeUntilExpiry / 1000 / 60), // minutes
        });
        return timeUntilExpiry < 5 * 60 * 1000;
    }

    async refreshToken(): Promise<string> {
        console.log("Refreshing token");
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            
            if (!refreshToken) {
                console.error("No refresh token in localStorage");
                throw new Error('No refresh token found');
            }
        
            console.log("Refreshing token");
            const response = await axios.post<RefreshResponse>(`${API_URL}/auth/refresh`, {
                refreshToken: refreshToken
            });
            
            // Store both tokens before returning
            const { token, refreshToken: newRefreshToken } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', newRefreshToken);
            
            return token;
        } catch (error) {
            console.error('Error refreshing token:', error);
            // Clear both tokens on error
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            throw error;
        }
    }

    getUserFromToken(): User | null {
        const token = this.getToken();
        const storedUser = localStorage.getItem(this.USER_KEY);
        
        if (!token) return null;

        try {
            const decoded = jwtDecode<JwtPayload>(token);
            
            const expirationTime = decoded.exp * 1000;
            const currentTime = Date.now();
            
            if (expirationTime <= currentTime) {
                this.removeToken();
                return null;
            }
            
            if (this.shouldRefreshToken(decoded)) {
                this.refreshToken().catch(console.error);
            }

            const businessRoles = decoded.businessRoles ? JSON.parse(decoded.businessRoles) : [];
            
            const user = storedUser ? JSON.parse(storedUser) : {
                id: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
                email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
                userName: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
                firstName: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] || '',
                lastName: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] || '',
            };

            return {
                ...user,
                businessId: decoded.BusinessId,
                businessRoles,
                emailConfirmed: true,
                phoneNumberConfirmed: false,
                twoFactorEnabled: false,
                lockoutEnabled: false,
                accessFailedCount: 0,
                isAdmin: decoded.IsAdmin?.toLowerCase() === 'true',
                business: null
            };
        } catch (error) {
            console.error('Error decoding token:', error);
            this.removeToken();
            return null;
        }
    }

    async login(email: string, password: string): Promise<LoginResponse> {
        // Clear existing theme data before login
        localStorage.removeItem(this.BUSINESS_THEME_KEY);
        localStorage.removeItem(this.BUSINESS_THEME_ID_KEY);

        const response = await axios.post<LoginResponse>(`${API_URL}/auth/login`, { email, password });
        const { token, refreshToken, user } = response.data;
        
        // Get expiration from response or calculate it from token
        const expiresAt = response.data.expiresAt || new Date(
            jwtDecode<JwtPayload>(token).exp * 1000
        ).toISOString();
        
        const decoded = jwtDecode<JwtPayload>(token);
        const businessRoles = decoded.businessRoles ? JSON.parse(decoded.businessRoles) : [];
        
        const enrichedUser = {
            ...user,
            businessId: decoded.BusinessId,
            businessRoles,
            isAdmin: decoded.IsAdmin?.toLowerCase() === 'true',
            business: null
        };
        
        // Clear and set new tokens and user data
        this.setToken(token);
        localStorage.setItem(this.USER_KEY, JSON.stringify(enrichedUser));
        localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
        localStorage.setItem(this.EXPIRES_AT_KEY, expiresAt);

        // If there's a business ID, fetch the theme
        let businessTheme: LoginResponse['businessTheme'] = undefined;
        if (enrichedUser.businessId) {
            try {
                const business = await businessService.getBusiness(enrichedUser.businessId);
                console.log('Business data:', business);
                businessTheme = {
                    backgroundColor: business.backgroundColor || DEFAULT_THEME.backgroundColor,
                    primaryColor: business.primaryColor || DEFAULT_THEME.primaryColor,
                    secondaryColor: business.secondaryColor || DEFAULT_THEME.secondaryColor,
                    navigationColor: business.navigationColor || DEFAULT_THEME.navigationColor,
                    textColor: business.textColor || DEFAULT_THEME.textColor,
                    accentColor: business.accentColor || DEFAULT_THEME.accentColor
                };
                localStorage.setItem(this.BUSINESS_THEME_KEY, JSON.stringify(businessTheme));
                localStorage.setItem(this.BUSINESS_THEME_ID_KEY, enrichedUser.businessId);
            } catch (error) {
                console.error('Failed to fetch business theme:', error);
            }
        }
        
        return {
            ...response.data,
            user: enrichedUser,
            businessTheme
        };
    }

    async register(email: string, password: string, firstName: string, lastName: string): Promise<LoginResponse> {
        const response = await axios.post<LoginResponse>(`${API_URL}/auth/register`, {
            email,
            password,
            firstName,
            lastName
        });

        const { token, refreshToken, user } = response.data;

        // Get expiration from response or calculate it from token
        const expiresAt = response.data.expiresAt || new Date(
            jwtDecode<JwtPayload>(token).exp * 1000
        ).toISOString();
        
        // Decode token to get business roles and other claims
        const decoded = jwtDecode<JwtPayload>(token);
        const businessRoles = decoded.businessRoles ? JSON.parse(decoded.businessRoles) : [];
        
        const enrichedUser = {
            ...user,
            businessId: decoded.BusinessId,
            businessRoles,
            isAdmin: decoded.IsAdmin?.toLowerCase() === 'true',
            business: null
        };

        this.setToken(token);
        localStorage.setItem(this.USER_KEY, JSON.stringify(enrichedUser));
        localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
        localStorage.setItem(this.EXPIRES_AT_KEY, expiresAt);

        // If there's a business ID, fetch the theme
        let businessTheme: LoginResponse['businessTheme'] = undefined;
        if (enrichedUser.businessId) {
            try {
                const business = await businessService.getBusiness(enrichedUser.businessId);
                console.log('Business data:', business);
                businessTheme = {
                    backgroundColor: business.backgroundColor || DEFAULT_THEME.backgroundColor,
                    primaryColor: business.primaryColor || DEFAULT_THEME.primaryColor,
                    secondaryColor: business.secondaryColor || DEFAULT_THEME.secondaryColor,
                    navigationColor: business.navigationColor || DEFAULT_THEME.navigationColor,
                    textColor: business.textColor || DEFAULT_THEME.textColor,
                    accentColor: business.accentColor || DEFAULT_THEME.accentColor
                };
                localStorage.setItem('businessTheme', JSON.stringify(businessTheme));
                localStorage.setItem('businessThemeId', enrichedUser.businessId);
            } catch (error) {
                console.error('Failed to fetch business theme:', error);
            }
        }
        
        return {
            ...response.data,
            user: enrichedUser,
            businessTheme
        };
    }

    logout(): void {
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.USER_KEY);
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
        localStorage.removeItem(this.EXPIRES_AT_KEY);
        localStorage.removeItem(this.BUSINESS_THEME_KEY);
        localStorage.removeItem(this.BUSINESS_THEME_ID_KEY);
    }

    isAuthenticated(): boolean {
        return !!this.getToken();
    }

    async getCurrentUser(): Promise<User> {
        try {
            const response = await apiClient.get('/auth/current-user');
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async refreshUserData(): Promise<{ user: User } | null> {
        try {
            const token = localStorage.getItem('token');
            if (!token) return null;
            
            const response = await fetch(`${API_URL}/auth/verify`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const userData = await response.json();
                return userData;
            }
            return null;
        } catch (error) {
            console.error('Error refreshing user data:', error);
            return null;
        }
    }

    // Email Authentication Methods
    async sendEmailVerification(email: string): Promise<{ success: boolean; message: string; errors?: string[] }> {
        try {
            const response = await axios.post(`${API_URL}/auth/send-email-verification`, { email });
            return response.data;
        } catch (error) {
            console.error('Error sending email verification:', error);
            if (axios.isAxiosError(error) && error.response) {
                return {
                    success: false,
                    message: error.response.data.message || 'Failed to send email verification',
                    errors: error.response.data.errors || []
                };
            }
            return {
                success: false,
                message: 'An unexpected error occurred',
                errors: ['Network error']
            };
        }
    }

    async verifyEmail(userId: string, token: string): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axios.post(`${API_URL}/auth/verify-email`, { userId, token });
            return response.data;
        } catch (error) {
            console.error('Error verifying email:', error);
            if (axios.isAxiosError(error) && error.response) {
                return {
                    success: false,
                    message: error.response.data.message || 'Email verification failed'
                };
            }
            return {
                success: false,
                message: 'An unexpected error occurred'
            };
        }
    }

    async sendPasswordReset(email: string): Promise<{ success: boolean; message: string; errors?: string[] }> {
        try {
            const response = await axios.post(`${API_URL}/auth/send-password-reset`, { email });
            return response.data;
        } catch (error) {
            console.error('Error sending password reset:', error);
            if (axios.isAxiosError(error) && error.response) {
                return {
                    success: false,
                    message: error.response.data.message || 'Failed to send password reset email',
                    errors: error.response.data.errors || []
                };
            }
            return {
                success: false,
                message: 'An unexpected error occurred',
                errors: ['Network error']
            };
        }
    }

    async resetPassword(email: string, token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axios.post(`${API_URL}/auth/reset-password`, { 
                email, 
                token, 
                newPassword 
            });
            return response.data;
        } catch (error) {
            console.error('Error resetting password:', error);
            if (axios.isAxiosError(error) && error.response) {
                return {
                    success: false,
                    message: error.response.data.message || 'Password reset failed'
                };
            }
            return {
                success: false,
                message: 'An unexpected error occurred'
            };
        }
    }
}

export const authService = new AuthService(); 